import { HttpClient } from "@utils/httpClient";
import type {
  AccessModule,
  AccessScope,
  ApiKey,
  ApiKeyCreate,
  AccessUserDetail,
  AuditFilters,
  AuditPage,
  CataloguePlan,
  EffectivePermissions,
  LicenceInfo,
  LicenceInstance,
  LicenceInstanceCreate,
  ModuleCreate,
  OrganisationCreate,
  OrganisationNode,
  PermissionCatalogue,
  PlanCreate,
  Role,
  RoleCreate,
  RoleUpdate,
  StepRun,
  UserAccessUpdate,
  UserCreate,
  UserCreateResult,
  UserAccess,
  UserPage,
  UserProfileUpdate,
} from "@models/access";

const BASE_URL = import.meta.env.VITE_XPLATFORM_CORE_API_BASE_URL;
const PREFIX = "/admin/access";

const query = (params: Record<string, string | number | boolean | null | undefined>): string => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== "") search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : "";
};

const id = (value: string) => encodeURIComponent(value);

export interface UserListFilters {
  organisationId: string;
  includeDescendants?: boolean;
  q?: string;
  status?: string;
  offset?: number;
  limit?: number;
}

/**
 * Users, roles and permissions of this environment's X-Platform application in
 * BeeHive, through core. Core confines every call to the DSML organisation tree
 * and to its own application; this client never names an application.
 */
class AccessApi {
  private readonly http: HttpClient;

  constructor() {
    if (!BASE_URL) throw new Error("VITE_XPLATFORM_CORE_API_BASE_URL is not configured");
    this.http = new HttpClient(BASE_URL);
  }

  private get<T>(path: string, signal?: AbortSignal): Promise<T> {
    return this.http.makeAuthenticatedRequest<T>(`${PREFIX}${path}`, { method: "GET", signal });
  }

  private send<T>(method: "POST" | "PUT" | "DELETE", path: string, body?: unknown): Promise<T> {
    return this.http.makeAuthenticatedRequest<T>(`${PREFIX}${path}`, {
      method,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  }

  scope(signal?: AbortSignal): Promise<AccessScope> {
    return this.get("/scope", signal);
  }

  organisationTree(signal?: AbortSignal): Promise<OrganisationNode[]> {
    return this.get("/organisations/tree", signal);
  }

  /** Always a direct child of the root organisation (core refuses anything else). */
  createOrganisation(body: OrganisationCreate): Promise<OrganisationNode> {
    return this.send("POST", "/organisations", body);
  }

  // Licence instances and API keys

  licences(signal?: AbortSignal): Promise<LicenceInfo[]> {
    return this.get("/licences", signal);
  }

  instances(organisationId: string, signal?: AbortSignal): Promise<LicenceInstance[]> {
    return this.get(`/organisations/${id(organisationId)}/instances`, signal);
  }

  createInstance(organisationId: string, body: LicenceInstanceCreate): Promise<LicenceInstance> {
    return this.send("POST", `/organisations/${id(organisationId)}/instances`, body);
  }

  apiKeys(instanceId: string, signal?: AbortSignal): Promise<ApiKey[]> {
    return this.get(`/instances/${id(instanceId)}/keys`, signal);
  }

  /** BeeHive mails the key to the acting admin; the response carries metadata only. */
  createApiKey(instanceId: string, body: ApiKeyCreate): Promise<ApiKey> {
    return this.send("POST", `/instances/${id(instanceId)}/keys`, body);
  }

  revokeApiKey(instanceId: string, keyId: string): Promise<void> {
    return this.send("DELETE", `/instances/${id(instanceId)}/keys/${id(keyId)}`);
  }

  // Roles

  roles(signal?: AbortSignal): Promise<Role[]> {
    return this.get("/roles", signal);
  }

  role(roleId: string, signal?: AbortSignal): Promise<Role> {
    return this.get(`/roles/${id(roleId)}`, signal);
  }

  roleUsers(roleId: string, offset = 0, limit = 50, signal?: AbortSignal): Promise<UserPage> {
    return this.get(`/roles/${id(roleId)}/users${query({ offset, limit })}`, signal);
  }

  createRole(body: RoleCreate): Promise<Role> {
    return this.send("POST", "/roles", body);
  }

  updateRole(roleId: string, body: RoleUpdate): Promise<Role> {
    return this.send("PUT", `/roles/${id(roleId)}`, body);
  }

  deleteRole(roleId: string): Promise<void> {
    return this.send("DELETE", `/roles/${id(roleId)}`);
  }

  // Users

  users(filters: UserListFilters, signal?: AbortSignal): Promise<UserPage> {
    return this.get(`/users${query({ ...filters })}`, signal);
  }

  user(userId: string, signal?: AbortSignal): Promise<AccessUserDetail> {
    return this.get(`/users/${id(userId)}`, signal);
  }

  effectivePermissions(userId: string, organisationId: string, signal?: AbortSignal): Promise<EffectivePermissions> {
    return this.get(`/users/${id(userId)}/effective-permissions${query({ organisationId })}`, signal);
  }

  /** 201 or 207: a 207 carries the created user and the failed access assignment. */
  createUser(body: UserCreate): Promise<UserCreateResult> {
    return this.send("POST", "/users", body);
  }

  /** Answers with the user's access rows for this application, not the whole user. */
  updateAccess(userId: string, body: UserAccessUpdate): Promise<UserAccess[]> {
    return this.send("PUT", `/users/${id(userId)}/access`, body);
  }

  updateProfile(userId: string, body: UserProfileUpdate): Promise<AccessUserDetail> {
    return this.send("PUT", `/users/${id(userId)}`, body);
  }

  // Permission catalogue

  permissions(signal?: AbortSignal): Promise<PermissionCatalogue> {
    return this.get("/permissions", signal);
  }

  /** A new, empty BeeHive module in the licence and the role template; 409 when the name exists in any case. */
  createModule(body: ModuleCreate): Promise<AccessModule> {
    return this.send("POST", "/modules", body);
  }

  /** Computes every step (reads only); nothing runs until a step is started. */
  createPlan(body: PlanCreate): Promise<CataloguePlan> {
    return this.send("POST", "/permissions/plans", body);
  }

  plan(planId: string, signal?: AbortSignal): Promise<CataloguePlan> {
    return this.get(`/permissions/plans/${id(planId)}`, signal);
  }

  runStep(planId: string, step: number, body: StepRun = {}): Promise<CataloguePlan> {
    return this.send("POST", `/permissions/plans/${id(planId)}/steps/${step}/run`, body);
  }

  cancelPlan(planId: string): Promise<CataloguePlan> {
    return this.send("POST", `/permissions/plans/${id(planId)}/cancel`);
  }

  /** Plans by status; `open` drives the "Resume" banner (one open plan at a time). */
  plans(status?: "open" | "done" | "cancelled", signal?: AbortSignal): Promise<CataloguePlan[]> {
    return this.get(`/permissions/plans${query({ status })}`, signal);
  }

  // Audit

  audit(filters: AuditFilters = {}, signal?: AbortSignal): Promise<AuditPage> {
    return this.get(`/audit${query({ ...filters })}`, signal);
  }
}

let instance: AccessApi | null = null;
export const accessApi = (): AccessApi => {
  instance ??= new AccessApi();
  return instance;
};
